export type BoatCategory = 'sin-licencia' | 'con-licencia' | 'premium'

export interface BoatPrice {
  /** Translation key OR literal duration label. Pass to t() at render time. */
  label: string
  price: string
}

export interface Boat {
  id: string
  name: string
  category: BoatCategory
  specs: {
    length?: string
    engine: string
    capacity: string
    licenseReq: boolean
    location: string
  }
  /** Translation key for the banner shown above the price block. Overrides default license label. */
  licenseBanner?: string
  /** Optional translation key for a warning shown inside the gradient panel. */
  noteText?: string
  /** Optional translation keys for the left-side spec lines. When present, replaces the default capacity/length lines. */
  specLines?: string[]
  /** Optional override for the WhatsApp template key. */
  waTemplateKey?: string
  prices: BoatPrice[]
  descKey: string
  /** Primary photo. Used as fallback when `gallery` is not provided. */
  image: string
  /** Optional gallery of photos. When present (and length > 1), the card renders a carousel. */
  gallery?: string[]
}

export const boats: Boat[] = [
  // SIN LICENCIA
  {
    id: 'marinello',
    name: 'Marinello',
    category: 'sin-licencia',
    specs: {
      length: '5',
      engine: '15 CV (40)',
      capacity: '6',
      licenseReq: false,
      location: 'Torrevieja',
    },
    prices: [
      { label: 'boat.duration.2h', price: '140€' },
      { label: 'boat.duration.4h', price: '200€' },
      { label: 'boat.duration.8h', price: '350€' },
    ],
    descKey: 'desc.marinello',
    image:
      'https://cdn.magicpatterns.com/uploads/vecDtz8EBmmM6dbtWrDESk/marinellofisher.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/vecDtz8EBmmM6dbtWrDESk/marinellofisher.jpg',
      'https://cdn.magicpatterns.com/uploads/rowAFsPEAi3fBWcptS5bqm/marinello_1.jpg',
    ],
  },
  {
    id: 'v2',
    name: 'V2',
    category: 'sin-licencia',
    specs: {
      length: '5',
      engine: '15 CV (40)',
      capacity: '6',
      licenseReq: false,
      location: 'Torrevieja · Cabo Roig',
    },
    prices: [
      { label: 'boat.duration.2h', price: '140€' },
      { label: 'boat.duration.4h', price: '200€' },
      { label: 'boat.duration.8h', price: '350€' },
    ],
    descKey: 'desc.v2',
    image:
      'https://cdn.magicpatterns.com/uploads/jf72YPYWay5pfJPt8vNmML/PHOTO-2026-06-27-11-53-21.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/jf72YPYWay5pfJPt8vNmML/PHOTO-2026-06-27-11-53-21.jpg',
      'https://cdn.magicpatterns.com/uploads/me4iwWwEFNbBvhVZXYonJr/V2-2.jpg',
    ],
  },
  {
    id: 'calima',
    name: 'Calima',
    category: 'sin-licencia',
    specs: {
      engine: '15 CV',
      capacity: '4',
      licenseReq: false,
      location: 'Torrevieja',
    },
    prices: [
      { label: 'boat.duration.2h', price: '125€' },
      { label: 'boat.duration.4h', price: '185€' },
      { label: 'boat.duration.8h', price: '255€' },
    ],
    descKey: 'desc.calima',
    image:
      'https://cdn.magicpatterns.com/uploads/oyPLiQAKCG5TeroQjH25si/calima-2.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/oyPLiQAKCG5TeroQjH25si/calima-2.jpg',
      'https://cdn.magicpatterns.com/uploads/v9rmM1rgoUtJEr5AiiuTBo/calima_1.jpg',
      'https://cdn.magicpatterns.com/uploads/fYd5QYSiHHq2wDd5h55UXv/calima_2.jpg',
    ],
  },
  // CON LICENCIA
  {
    id: 'invictus-fx190',
    name: 'Invictus FX190',
    category: 'con-licencia',
    specs: {
      engine: '100 CV',
      capacity: '8',
      licenseReq: true,
      location: 'Torrevieja · Cabo Roig · Mar Menor',
    },
    licenseBanner: 'boat.banner.licenseSkipper',
    prices: [
      { label: 'boat.duration.halfDay', price: '225€' },
      { label: 'boat.duration.fullDay', price: '325€' },
    ],
    descKey: 'desc.invictus',
    image:
      'https://cdn.magicpatterns.com/uploads/kXLFWgmnqkmsgJcHndvfaY/INVICTUS-alquilar-barco.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/kXLFWgmnqkmsgJcHndvfaY/INVICTUS-alquilar-barco.jpg',
      'https://cdn.magicpatterns.com/uploads/8qvg5TPEtF3VwuZakZWMgV/invictus.jpg',
    ],
  },
  {
    id: 'commander-600',
    name: 'Commander 600',
    category: 'con-licencia',
    specs: {
      engine: '115 CV',
      capacity: '6',
      licenseReq: true,
      location: 'Torrevieja · Alicante · Tabarca',
    },
    licenseBanner: 'boat.banner.licenseSkipper',
    prices: [
      { label: 'boat.duration.halfDay', price: '195€' },
      { label: 'boat.duration.fullDay', price: '295€' },
    ],
    descKey: 'desc.commander',
    image:
      'https://cdn.magicpatterns.com/uploads/1ViGGjBKCHvow7ygkKtmgu/Alquiler-barco-torrevieja-commander.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/1ViGGjBKCHvow7ygkKtmgu/Alquiler-barco-torrevieja-commander.jpg',
      'https://cdn.magicpatterns.com/uploads/nwMcvi4zA2Krwaa6AUFJSE/comander_1.jpg',
      'https://cdn.magicpatterns.com/uploads/qSFY4HYxhYv8MgLYQznjFE/comander_2.jpg',
    ],
  },
  {
    id: 'flyer',
    name: 'Flyer',
    category: 'con-licencia',
    specs: {
      engine: '150 CV',
      capacity: '8',
      licenseReq: true,
      location: 'Torrevieja · Tabarca',
    },
    licenseBanner: 'boat.banner.licensePNBSkipper',
    prices: [
      { label: 'boat.duration.halfDay', price: '245€' },
      { label: 'boat.duration.fullDay', price: '360€' },
    ],
    descKey: 'desc.flyer',
    image:
      'https://cdn.magicpatterns.com/uploads/gF99BSzC7JENoS6ic1fRiy/flyer.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/gF99BSzC7JENoS6ic1fRiy/flyer.jpg',
      'https://cdn.magicpatterns.com/uploads/j8uP1qVGe1Scee2GsdxSGv/flayer.jpg',
    ],
  },
  {
    id: 'nireus',
    name: 'Nireus',
    category: 'con-licencia',
    specs: {
      length: '6',
      engine: '200 CV',
      capacity: '12',
      licenseReq: true,
      location: 'Torrevieja',
    },
    licenseBanner: 'boat.banner.licenseSkipper',
    prices: [
      { label: 'boat.duration.2h', price: '220€' },
      { label: 'boat.duration.4h', price: '300€' },
      { label: 'boat.duration.8h', price: '390€' },
    ],
    descKey: 'desc.nireus',
    image:
      'https://cdn.magicpatterns.com/uploads/k7G6N3wurqN7mXteosNA1Z/IMG_5840_2.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/k7G6N3wurqN7mXteosNA1Z/IMG_5840_2.jpg',
      'https://cdn.magicpatterns.com/uploads/gdiiYWY5WarkCttaCXdzrr/IMG_5841_2.jpg',
      'https://cdn.magicpatterns.com/uploads/7B6H6LEDRCzeMuUPnemFJi/IMG_5842_2.jpg',
      'https://cdn.magicpatterns.com/uploads/gMkU61VwHuCtm7DtjZDxT9/IMG_5843_2.jpg',
      'https://cdn.magicpatterns.com/uploads/fvU62pfpwnKLASH6WGwXZd/nireus_4.jpg',
      'https://cdn.magicpatterns.com/uploads/d63MXzGAZ7ifby9uKiXUSN/nireus.jpg',
      'https://cdn.magicpatterns.com/uploads/cskXvu7nPZiyzXAneLt1fF/nireus_3.jpg',
    ],
  },
  {
    id: 'ranieri',
    name: 'Ranieri',
    category: 'con-licencia',
    specs: {
      length: '7,8',
      engine: '225 CV',
      capacity: '9',
      licenseReq: true,
      location: 'Torrevieja',
    },
    licenseBanner: 'boat.banner.licensePNBSkipper',
    prices: [
      { label: 'boat.duration.4h', price: '350€' },
      { label: 'boat.duration.8h', price: '550€' },
    ],
    descKey: 'desc.ranieri',
    image:
      'https://cdn.magicpatterns.com/uploads/e83M3Va28sGhAnDFxz2hax/PHOTO-2026-06-27-11-55-01.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/e83M3Va28sGhAnDFxz2hax/PHOTO-2026-06-27-11-55-01.jpg',
      'https://cdn.magicpatterns.com/uploads/jHcDKg27NAH8BkvPxWsEaM/ranieri-1.jpg',
    ],
  },
  {
    id: 'smiler',
    name: 'Smiler',
    category: 'con-licencia',
    specs: {
      length: '6',
      engine: '115 CV',
      capacity: '8',
      licenseReq: true,
      location: 'Torrevieja',
    },
    licenseBanner: 'boat.banner.licenseSkipper',
    prices: [
      { label: 'boat.duration.4h', price: '280€' },
      { label: 'boat.duration.8h', price: '380€' },
    ],
    descKey: 'desc.smiler',
    image:
      'https://cdn.magicpatterns.com/uploads/pBv3V24hARTJt8C8gEQEgq/smiler2.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/pBv3V24hARTJt8C8gEQEgq/smiler2.jpg',
      'https://cdn.magicpatterns.com/uploads/esJw7xMd5YJAeVMWFzbHsT/smiler.jpg',
    ],
  },
  {
    id: 'sea-ray-290-sundeck',
    name: 'Sea Ray 290 Sundeck',
    category: 'premium',
    specs: {
      length: '9',
      engine: 'Bi-motor',
      capacity: '11',
      licenseReq: false,
      location: 'Torrevieja',
    },
    licenseBanner: 'boat.banner.skipperIncluded',
    specLines: [
      'boat.specLines.searay.peopleSkipper',
      'boat.specLines.searay.paddleIncluded',
    ],
    prices: [
      { label: 'boat.duration.2h', price: '250€' },
      { label: 'boat.duration.halfDay', price: '550€' },
      { label: 'boat.duration.fullDay', price: '899€' },
    ],
    descKey: 'desc.searay',
    waTemplateKey: 'wa.searay',
    image:
      'https://cdn.magicpatterns.com/uploads/xxbnow23j3tmTStUGN1SP2/sea_ray.jpg',
  },
  {
    id: 'prestige',
    name: 'Prestige',
    category: 'premium',
    specs: {
      length: '13',
      engine: 'Bi-motor',
      capacity: '12',
      licenseReq: false,
      location: 'Torrevieja',
    },
    licenseBanner: 'boat.banner.skipperIncluded',
    specLines: [
      'boat.specLines.prestige.people',
      'boat.specLines.prestige.features',
    ],
    prices: [
      { label: 'boat.duration.2h', price: '350€' },
      { label: 'boat.duration.halfDay', price: '650€' },
      { label: 'boat.duration.fullDay', price: '1.150€' },
    ],
    descKey: 'premium.boat.prestige.desc',
    image:
      'https://cdn.magicpatterns.com/uploads/s6NEVnPPQV1nKdqRxPb3jz/PHOTO-2026-06-27-11-49-05.jpg',
    gallery: [
      'https://cdn.magicpatterns.com/uploads/s6NEVnPPQV1nKdqRxPb3jz/PHOTO-2026-06-27-11-49-05.jpg',
      'https://cdn.magicpatterns.com/uploads/ed2eJZHysnQTet2hSrEgMq/PHOTO-2026-06-27-11-49-05_2.jpg',
      'https://cdn.magicpatterns.com/uploads/3KcLZbQNiR6LLtZNwjH9Fh/PHOTO-2026-06-27-11-49-05_3.jpg',
      'https://cdn.magicpatterns.com/uploads/hBimH7jnhALaLbtxAF6zC6/PHOTO-2026-06-27-11-49-05.jpg',
    ],
  },

  // Premium boats (Monaco, Diamonte, Lagoon 450, Pura Vida) live exclusively
  // in the dedicated PremiumFleet section — they are intentionally NOT listed here.
]
